import classNames from 'classnames';
import { Fragment } from 'react';

import { Heading, Icon } from '@/components';
import type { LineItem } from '__generated__/graphql';
import { currencyFormat, formatEnUSPhoneNumber } from '@/utils';

import { orderLastStatus, orderStatus } from './constant';
import ItemList from './ItemList';
import OrderRouteMap from './OrderRouteMap';
import type {
  OrderDetailAddress,
  OrderDetailDelivery,
  OrderDetailOrder,
} from './types';
import styles from './orderdetail.module.scss';
import { useEcomStoreSelector } from '@/data';
import { useFeaturedFlag } from '@/hooks';

type OrderStatusTimelineProps = {
  delivery: boolean;
  status?: string | null;
};

const OrderStatusTimeline = ({
  delivery,
  status,
}: OrderStatusTimelineProps) => {
  const defaultFinishedStates = orderLastStatus.filter(state => state.default);
  const activeFinishedStates = orderLastStatus.filter(
    state => state.name === status,
  );
  const hasFinalActiveState = activeFinishedStates.length > 0;

  const states = (delivery ? orderStatus.delivery : orderStatus.pickup).concat(
    activeFinishedStates.length ? activeFinishedStates : defaultFinishedStates,
  );

  const indexProgress = states.findIndex(state => state.name === status);

  const colorDots = (position: number) => {
    if (position < indexProgress) return '#0b3b4a';
    return '#b6c4c9';
  };

  return (
    <section>
      <Heading className={styles['order__section-heading']} level={3}>
        Order status
      </Heading>
      <div className={styles['order-details__status']}>
        <ol>
          {states.map((state, position) => {
            const renderDots = position < states.length - 1;
            const active =
              state.name === status ||
              (indexProgress === -1 && position === 0 && !hasFinalActiveState);

            return (
              <Fragment key={`status-${state.name}-${position}`}>
                <li
                  className={classNames(styles['order-details__status-step'], {
                    [styles.active]: active,
                  })}
                >
                  <figure
                    className={classNames({
                      [styles.active]: active,
                    })}
                  >
                    <Icon name={state.icon} />
                  </figure>
                  <span className={styles['order-details__status-label']}>
                    {state.label}
                  </span>
                </li>
                {renderDots && (
                  <li
                    className={styles.dotted}
                    style={{
                      backgroundImage: `radial-gradient(${colorDots(
                        position,
                      )} 2.5px, transparent 0)`,
                    }}
                  />
                )}
              </Fragment>
            );
          })}
        </ol>
      </div>
    </section>
  );
};

type OrderItemsSectionProps = {
  items: Array<LineItem | null> | null | undefined;
};

const OrderItemsSection = ({ items }: OrderItemsSectionProps) => {
  const safeItems = (items ?? []).filter((item): item is LineItem => !!item);

  return (
    <section>
      <Heading className={styles['order__section-heading']} level={3}>
        Ordered items
      </Heading>
      <ItemList items={safeItems} />
    </section>
  );
};

type OrderPaymentSummaryProps = {
  deliveryDetails?: OrderDetailDelivery | null;
  order: OrderDetailOrder;
};

const toCurrencyLabel = (value?: number | null, fallback = 'Free') => {
  if (!value) return fallback;
  return `$${currencyFormat(value)}`;
};

const OrderPaymentSummary = ({
  deliveryDetails,
  order,
}: OrderPaymentSummaryProps) => (
  <section>
    <Heading className={styles['order__section-heading']} level={3}>
      Payment details
    </Heading>
    <div className={styles.order__summary}>
      <div className={styles['order__summary-body']}>
        <p className={styles['order__summary-item']}>
          <strong>Sub-total</strong>
          <span>{toCurrencyLabel(order.subtotal_price, '$0')}</span>
        </p>
        {!deliveryDetails?.pickup && (
          <p className={styles['order__summary-item']}>
            <strong>Delivery fee</strong>
            <span>{toCurrencyLabel(order.total_delivery)}</span>
          </p>
        )}
        <p className={styles['order__summary-item']}>
          <strong>Taxes</strong>
          <span>
            {order.total_tax
              ? `$${currencyFormat(order.total_tax)}`
              : 'Included'}
          </span>
        </p>
        {(order.total_discounts ?? 0) > 0 ? (
          <p className={styles['order__summary-item']}>
            <strong>Discounts</strong>
            <span>{`- $${currencyFormat(order.total_discounts ?? 0)}`}</span>
          </p>
        ) : null}
        <p
          className={`${styles['order__summary-item']} ${styles['order__summary-item-total']}`}
        >
          <strong>Total paid</strong>
          <span>{toCurrencyLabel(order.total_price, '$0')}</span>
        </p>
      </div>
    </div>
  </section>
);

type OrderAddressesSectionProps = {
  address?: OrderDetailAddress | null;
};

type OrderNotesSectionProps = {
  notes?: string | null;
};

type OrderAddressSectionProps = {
  address?: OrderDetailAddress | null;
  deliveryDetails?: OrderDetailDelivery | null;
};

const OrderAddressesSection = ({ address }: OrderAddressesSectionProps) => {
  const { email } = useEcomStoreSelector(['email']);
  const clientName = `${address?.first_name ?? ''} ${
    address?.last_name ?? ''
  }`.trim();
  const addressLine = address?.address1 ?? '';
  const addressApt = address?.address2 ?? '';
  const partialAddress = [
    address?.city,
    address?.province ?? address?.province_code,
    address?.country,
  ]
    .filter(Boolean)
    .join(', ');
  const addressLoc =
    partialAddress && address?.zip
      ? `${partialAddress} - ${address.zip}`
      : partialAddress;

  return (
    <div className={styles['order__info-thankyou']}>
      <p className={styles['order__billing-item']}>
        {clientName || 'Not Assigned yet'}
      </p>
      <p className={styles['order__billing-item']}>
        {email || 'Not Assigned yet'}
      </p>
      <p className={styles['order__billing-item-space']}>
        {address?.phone
          ? formatEnUSPhoneNumber(address.phone)
          : 'Not Assigned yet'}
      </p>
      <p className={styles['order__billing-item-upper-space']}>
        {addressLine || 'Not Assigned yet'}
      </p>
      {addressApt ? (
        <p className={styles['order__billing-item']}>{addressApt}</p>
      ) : null}
      <p className={styles['order__billing-item']}>
        {addressLoc || 'Not Assigned yet'}
      </p>
    </div>
  );
};

const OrderNotesSection = ({ notes }: OrderNotesSectionProps) => {
  return (
    <div className={styles['order__notes-section']}>
      <Heading className={styles['order__section-heading']} level={3}>
        Notes
      </Heading>
      <p className={styles['order__notes-body']}>
        {notes || 'No additional notes specified.'}
      </p>
    </div>
  );
};

const OrderAddressSection = ({
  address,
  deliveryDetails,
}: OrderAddressSectionProps) => {
  return (
    <section>
      <Heading className={styles['order__section-heading']} level={3}>
        Billing and shipping
      </Heading>
      <div className={styles['order__info-route-map']}>
        <OrderAddressesSection address={address} />
        <OrderRouteMapSection
          address={address}
          deliveryDetails={deliveryDetails}
        />
      </div>
    </section>
  );
};

type OrderRouteMapSectionProps = {
  address?: OrderDetailAddress | null;
  deliveryDetails?: OrderDetailDelivery | null;
};

const OrderRouteMapSection = ({
  address,
  deliveryDetails,
}: OrderRouteMapSectionProps) => {
  const { hideMap } = useFeaturedFlag();

  const latitude = address?.latitude ?? null;
  const longitude = address?.longitude ?? null;

  const hasAddressCoordinates =
    typeof latitude === 'number' && typeof longitude === 'number';

  if (hideMap) {
    return null;
  }

  return (
    <OrderRouteMap
      address1={{
        ...(address ?? {}),
        title: 'Destination of your order',
      }}
      address2={
        hasAddressCoordinates && deliveryDetails?.delivery
          ? {
              // TODO: Replace with actual driver location when available.
              latitude: latitude - 0.05,
              longitude: longitude - 0.05,
              title: 'Current position of your order',
            }
          : null
      }
      className={styles.order__map}
    />
  );
};

export {
  OrderAddressSection,
  OrderAddressesSection,
  OrderNotesSection,
  OrderItemsSection,
  OrderPaymentSummary,
  OrderRouteMapSection,
  OrderStatusTimeline,
};
